import React from 'react';
import { View, Flex, ToastQueue } from '@adobe/react-spectrum';
import ChevronIcon from 'app/assets/svgIcons/ChevronIcon';
import GeneralInfoCommentIcon from '../CommentPanel/GeneralInfoCommentIcon/GeneralCommentIcon';
import { EditOnlyWrapper } from '../context/RBACContext';
import { AiAssistantSection } from '../AiAssistantSection';
import { Decisions } from '../Decisions/Decisions';
import { DACIContacts } from '../DACIContacts/DACIContacts';
import AdditionalInfo from '../AdditionalInfo/AdditionalInfo';
import { ChangeLogSection } from '../ChangeLog/ChangeLog';
import FeaturesSectionReadonlyWrapper from '../FeaturesSectionWrapper/FeaturesSectionReadonlyWrapper';
import { SlideoutFeaturesContainerSection } from './SlideoutFeaturesSection';
import {
  SlideoutMilestoneSortAndMenu,
  SlideoutMilestoneAddFooterSlot
} from './SlideoutMilestonesSection';
import type { SlideOutPanelCard } from '../types/slideOutPanel.types';
import type { MilestoneSortPreferenceChoice } from '../hooks/useMilestoneSortPreference';
import type { AdditionalInfoRef } from '../AdditionalInfo/AdditionalInfo';
import type { FeaturesContainerRef } from '../Features';
import type { PhaseOrderEntry } from '../../AlertsAndNotification/utils/alertScrollPriority';
import styles from '../SlideOutPanel.module.scss';

export type SlideoutMainContentSectionProps = {
  activeSection: string;
  canAccessAiAssistant: boolean;
  hasPhases: boolean;
  phasedAccordion: React.ReactNode;
  nonPhasedAccordion: React.ReactNode;
  featuresContent: React.ReactNode;

  areAllSectionsCollapsed: () => boolean;
  hasMomentLevelMilestones: boolean;
  isMilestonesExpanded: boolean;
  toggleMilestones: () => void;
  handleGeneralInfoCommentIconClick: (params: { phaseName: string }) => void;
  handleSortToggle: (phaseKey: string) => void;
  getSortDescriptiveText: (phaseKey: string) => string;
  sortDropdownOpen: Record<string, boolean>;
  getEffectiveMilestoneSort: (phaseKey: string) => string;
  handleSortSelect: (key: string, sort: MilestoneSortPreferenceChoice) => void;
  getMilestoneArray: () => any[];
  milestoneUpdateTrigger: number;
  milestoneMenuOpen: Record<string, boolean>;
  handleMilestoneMenuToggle: (menuKey: string) => void;
  handleDeleteMilestones: (
    phaseId?: string,
    phaseName?: string
  ) => Promise<void>;
  card: SlideOutPanelCard | null | undefined;
  renderMilestoneMatrix: () => React.ReactNode;
  shouldShowAddMilestoneButton: (phaseId?: string) => boolean;
  getMilestones: (phaseId?: string) => any;
  seamlessRefreshAndNotifications: (patchResponse?: unknown) => void;
  momentName: string;
  momentType: string;
  canEditMilestones: boolean;

  isOverviewExpanded: boolean;
  toggleOverview: () => void;
  renderOverviewSection: () => React.ReactNode;

  getDataWithFallback: () => unknown;
  featuresRef: React.Ref<any>;
  featuresContainerRef: React.Ref<FeaturesContainerRef>;
  targetFeatureId?: string | null;
  targetProductId?: string | null;
  navigationContext: { type: string };
  printMode?: boolean;
  handleSeamlessDataRefreshWithoutFeatures: (patchResponse?: unknown) => void;
  refreshMomentNotifications: () => void;

  isDecisionsExpanded: boolean;
  toggleDecisions: () => void;
  handleOpenDecisionPopup: () => void;
  addDecisionRef: React.Ref<any>;
  setErrorMessage: React.Dispatch<React.SetStateAction<string>>;
  setShowErrorBanner: React.Dispatch<React.SetStateAction<boolean>>;
  seamlessRefreshWithoutFeatures: (patchResponse?: unknown) => void;

  isDACIExpanded: boolean;
  toggleDACI: () => void;
  handleOpenDACIPopup: () => void;
  daciContacts: any;
  handleAddDACIContact: (contact: any) => void;
  handleEditDACIContact: (updatedContact: any, originalContact: any) => void;
  handleDeleteDACIContact: (contactToDelete: any) => void;

  isAdditionalInfoExpanded: boolean;
  toggleAdditionalInfo: () => void;
  handleOpenAdditionalInfoPopup: () => void;
  additionalInfoRef: React.Ref<AdditionalInfoRef>;
  apiDataUpdater: {
    addAdditionalInfoLink: (newLink: any) => Promise<unknown>;
    updateAdditionalInfoLink: (updatedLink: any) => Promise<unknown>;
    deleteAdditionalInfoLink: (linkId: string) => Promise<unknown>;
  };

  isChangeLogExpanded: boolean;
  toggleChangeLog: () => void;
  isOpen: boolean;
  /** Phase display order for this moment ([] for non-phased) — see alertScrollPriority.ts. */
  phases: PhaseOrderEntry[];
};

/**
 * Main slideout body: content shell, AI/view/features tabs, additional sections (Part 3A-3).
 */
export function SlideoutMainContentSection({
  activeSection,
  canAccessAiAssistant,
  hasPhases,
  phasedAccordion,
  nonPhasedAccordion,
  featuresContent,
  areAllSectionsCollapsed,
  hasMomentLevelMilestones,
  isMilestonesExpanded,
  toggleMilestones,
  handleGeneralInfoCommentIconClick,
  handleSortToggle,
  getSortDescriptiveText,
  sortDropdownOpen,
  getEffectiveMilestoneSort,
  handleSortSelect,
  getMilestoneArray,
  milestoneUpdateTrigger,
  milestoneMenuOpen,
  handleMilestoneMenuToggle,
  handleDeleteMilestones,
  card,
  renderMilestoneMatrix,
  shouldShowAddMilestoneButton,
  getMilestones,
  seamlessRefreshAndNotifications,
  momentName,
  momentType,
  canEditMilestones,
  isOverviewExpanded,
  toggleOverview,
  renderOverviewSection,
  getDataWithFallback,
  featuresRef,
  featuresContainerRef,
  targetFeatureId,
  targetProductId,
  navigationContext,
  printMode,
  handleSeamlessDataRefreshWithoutFeatures,
  refreshMomentNotifications,
  isDecisionsExpanded,
  toggleDecisions,
  handleOpenDecisionPopup,
  addDecisionRef,
  setErrorMessage,
  setShowErrorBanner,
  seamlessRefreshWithoutFeatures,
  isDACIExpanded,
  toggleDACI,
  handleOpenDACIPopup,
  daciContacts,
  handleAddDACIContact,
  handleEditDACIContact,
  handleDeleteDACIContact,
  isAdditionalInfoExpanded,
  toggleAdditionalInfo,
  handleOpenAdditionalInfoPopup,
  additionalInfoRef,
  apiDataUpdater,
  isChangeLogExpanded,
  toggleChangeLog,
  isOpen,
  phases
}: SlideoutMainContentSectionProps) {
  return (
    <View
      data-testid="slideout-content-container"
      UNSAFE_className={`${styles.contentContainer} ${
        activeSection === 'ai' ? styles.contentContainerAi : ''
      }`}
    >
      <div
        data-testid="slideout-content-wrapper"
        className={styles.contentWrapper}
      >
        <div
          data-testid="slideout-content-section"
          className={`${styles.contentSection} ${
            activeSection === 'ai' ? styles.contentSectionAi : ''
          }`}
        >
          {activeSection === 'ai' && canAccessAiAssistant && (
            <AiAssistantSection />
          )}
          {activeSection === 'view' && (
            <>
              {hasPhases ? (
                <>
                  {phasedAccordion}
                  <div
                    className={`${styles.additionalSections} ${
                      areAllSectionsCollapsed() ? styles.allCollapsed : ''
                    }`}
                  >
                    {/* Milestones section - separate collapsible section for moment-level milestones (e.g. Price Changes and Offers) */}
                    {hasMomentLevelMilestones && (
                      <div className={styles.section}>
                        <div
                          className={`${styles.sectionHeader} ${
                            isMilestonesExpanded
                              ? styles.sectionHeaderExpanded
                              : ''
                          }`}
                          onClick={toggleMilestones}
                          style={{ cursor: 'pointer' }}
                        >
                          <div className={styles.phaseToggle}>
                            <ChevronIcon isExpanded={isMilestonesExpanded} />
                          </div>
                          <Flex
                            direction="row"
                            alignItems="center"
                            UNSAFE_style={{ flex: 1 }}
                          >
                            <span className={styles.sectionTitle}>
                              Milestones
                            </span>
                            <GeneralInfoCommentIcon
                              phaseName="Milestones"
                              onClick={(e?: React.MouseEvent) => {
                                e?.stopPropagation();
                                handleGeneralInfoCommentIconClick({
                                  phaseName: 'Milestones'
                                });
                              }}
                            />
                          </Flex>
                          <SlideoutMilestoneSortAndMenu
                            sortKey={`nonphased-${card?.issueId}`}
                            sortDropdownOpen={sortDropdownOpen}
                            getSortDescriptiveText={getSortDescriptiveText}
                            getEffectiveMilestoneSort={
                              getEffectiveMilestoneSort
                            }
                            handleSortToggle={handleSortToggle}
                            handleSortSelect={handleSortSelect}
                            dateSortLabel="Date"
                            milestones={getMilestoneArray()}
                            menuKey={`nonphased-menu-${card?.issueId}`}
                            menuReactKey={`milestone-menu-${milestoneUpdateTrigger}`}
                            momentName={card?.name}
                            milestoneMenuOpen={milestoneMenuOpen}
                            handleMilestoneMenuToggle={
                              handleMilestoneMenuToggle
                            }
                            handleDeleteMilestones={handleDeleteMilestones}
                            stopPropagation
                          />
                        </div>
                        {isMilestonesExpanded && (
                          <div
                            className={styles.sectionContent}
                            style={{ paddingLeft: 24 }}
                          >
                            <div className={styles.milestonesContent}>
                              {renderMilestoneMatrix()}
                            </div>
                            <SlideoutMilestoneAddFooterSlot
                              visible={
                                shouldShowAddMilestoneButton() &&
                                !!(getMilestones()?.issueId || card?.issueId)
                              }
                              onDataRefresh={seamlessRefreshAndNotifications}
                              issueId={
                                getMilestones()?.issueId || card?.issueId!
                              }
                              milestoneId={getMilestones()?.milestoneId}
                              momentName={momentName}
                              momentType={momentType}
                              disabled={!canEditMilestones}
                            />
                          </div>
                        )}
                      </div>
                    )}

                    <div className={styles.section}>
                      <div
                        className={`${styles.sectionHeader} ${
                          isOverviewExpanded ? styles.sectionHeaderExpanded : ''
                        }`}
                        onClick={toggleOverview}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className={styles.phaseToggle}>
                          <ChevronIcon isExpanded={isOverviewExpanded} />
                        </div>

                        <Flex direction="row" alignItems="center">
                          <span className={styles.sectionTitle}>Overview</span>
                          <GeneralInfoCommentIcon
                            phaseName={'Overview'}
                            onClick={() =>
                              handleGeneralInfoCommentIconClick({
                                phaseName: 'Overview'
                              })
                            }
                          />
                        </Flex>
                      </div>
                      {isOverviewExpanded && (
                        <div className={styles.sectionContent}>
                          {renderOverviewSection()}
                        </div>
                      )}
                    </div>

                    {/* Features section - after Overview and before Decisions */}
                    {(() => {
                      const data = getDataWithFallback() as any;
                      const s1 = Array.isArray((data as any)?.surfaces)
                        ? (data as any).surfaces
                        : [];
                      const s2 = Array.isArray(
                        (data as any)?.generalInfo?.surfaces
                      )
                        ? (data as any).generalInfo.surfaces
                        : [];
                      const merged = [...s1, ...s2];
                      const momentDefaultSurfaces = Array.from(
                        new Set(
                          merged.filter(
                            (s: any) => typeof s === 'string' && s.trim() !== ''
                          )
                        )
                      );

                      return (
                        <SlideoutFeaturesContainerSection
                          featuresRef={featuresRef}
                          featuresContainerRef={featuresContainerRef}
                          issueId={card?.issueId}
                          momentName={card?.name || card?.title}
                          defaultSurfaces={momentDefaultSurfaces}
                          targetFeatureId={targetFeatureId ?? null}
                          targetProductId={targetProductId ?? null}
                          forceExpanded={
                            navigationContext.type === 'feature-click'
                          }
                          printMode={printMode}
                          onCommentIconClick={() =>
                            handleGeneralInfoCommentIconClick({
                              phaseName: 'Features'
                            })
                          }
                          onDataRefresh={
                            handleSeamlessDataRefreshWithoutFeatures
                          }
                          onNotificationRefresh={refreshMomentNotifications}
                          isOpen={isOpen}
                          phases={phases}
                        />
                      );
                    })()}

                    <div className={styles.section}>
                      <div
                        className={`${styles.sectionHeader} ${
                          isDecisionsExpanded
                            ? styles.sectionHeaderExpanded
                            : ''
                        }`}
                        style={{ cursor: 'pointer' }}
                      >
                        <div
                          onClick={toggleDecisions}
                          style={{
                            display: 'flex',
                            alignItems: 'center'
                          }}
                        >
                          <div className={styles.phaseToggle}>
                            <ChevronIcon isExpanded={isDecisionsExpanded} />
                          </div>

                          <Flex direction="row" alignItems="center">
                            <span className={styles.sectionTitle}>
                              Decisions
                            </span>
                            <GeneralInfoCommentIcon
                              phaseName={'Decisions'}
                              onClick={() =>
                                handleGeneralInfoCommentIconClick({
                                  phaseName: 'Decisions'
                                })
                              }
                            />
                          </Flex>
                        </div>
                        {isDecisionsExpanded && (
                          <EditOnlyWrapper>
                            <div className={styles.sectionEditIcon}>
                              <button
                                className={styles.addButton}
                                onClick={e => {
                                  e.stopPropagation();
                                  handleOpenDecisionPopup();
                                }}
                              >
                                <svg
                                  width="16"
                                  height="16"
                                  viewBox="0 0 16 16"
                                  fill="none"
                                >
                                  <path
                                    d="M8 3V13M3 8H13"
                                    stroke="#909090"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                  />
                                </svg>
                              </button>
                            </div>
                          </EditOnlyWrapper>
                        )}
                      </div>
                      {/* Always render Decisions component so the ref is available */}
                      <Decisions
                        decisions={
                          (getDataWithFallback() as any)?.decisions || []
                        }
                        issueId={card?.issueId}
                        addDecisionRef={addDecisionRef as any}
                        isExpanded={isDecisionsExpanded}
                        momentName={momentName}
                        momentType={momentType}
                        onError={error => {
                          setErrorMessage(error);
                          setShowErrorBanner(true);
                          // Auto-hide error banner after 5 seconds
                          setTimeout(() => {
                            setShowErrorBanner(false);
                            setErrorMessage('');
                          }, 5000);
                        }}
                        onDataRefresh={seamlessRefreshWithoutFeatures}
                      />
                    </div>

                    <div className={styles.section}>
                      <div
                        className={`${styles.sectionHeader} ${
                          isDACIExpanded ? styles.sectionHeaderExpanded : ''
                        }`}
                        style={{ cursor: 'pointer' }}
                      >
                        <div
                          onClick={toggleDACI}
                          style={{
                            display: 'flex',
                            alignItems: 'center'
                          }}
                        >
                          <div className={styles.phaseToggle}>
                            <ChevronIcon isExpanded={isDACIExpanded} />
                          </div>

                          <Flex direction="row" alignItems="center">
                            <span className={styles.sectionTitle}>
                              DACI Contacts
                            </span>
                            <GeneralInfoCommentIcon
                              phaseName={'DACI Contacts'}
                              onClick={() =>
                                handleGeneralInfoCommentIconClick({
                                  phaseName: 'DACI Contacts'
                                })
                              }
                            />
                          </Flex>
                        </div>
                        <EditOnlyWrapper>
                          <div className={styles.sectionEditIcon}>
                            <button
                              className={styles.addButton}
                              onClick={e => {
                                e.stopPropagation();
                                handleOpenDACIPopup();
                              }}
                            >
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none"
                              >
                                <path
                                  d="M8 3V13M3 8H13"
                                  stroke="#909090"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                />
                              </svg>
                            </button>
                          </div>
                        </EditOnlyWrapper>
                      </div>
                      {/* Always render DACIContacts component so the popup is available */}
                      <DACIContacts
                        daciData={daciContacts}
                        isExpanded={isDACIExpanded}
                        onAddContact={handleAddDACIContact}
                        onEditContact={handleEditDACIContact}
                        onDeleteContact={handleDeleteDACIContact}
                      />
                    </div>

                    <div className={styles.section}>
                      <div
                        className={`${styles.sectionHeader} ${
                          isAdditionalInfoExpanded
                            ? styles.sectionHeaderExpanded
                            : ''
                        }`}
                        style={{ cursor: 'pointer' }}
                      >
                        <div
                          onClick={toggleAdditionalInfo}
                          style={{
                            display: 'flex',
                            alignItems: 'center'
                            // flex: 1
                          }}
                        >
                          <div className={styles.phaseToggle}>
                            <ChevronIcon
                              isExpanded={isAdditionalInfoExpanded}
                            />
                          </div>
                          <Flex direction="row" alignItems="center">
                            <span className={styles.sectionTitle}>
                              Additional Info
                            </span>
                            <GeneralInfoCommentIcon
                              phaseName={'Additional Info'}
                              onClick={() =>
                                handleGeneralInfoCommentIconClick({
                                  phaseName: 'Additional Info'
                                })
                              }
                            />
                          </Flex>
                        </div>
                        <EditOnlyWrapper>
                          <div className={styles.sectionEditIcon}>
                            <button
                              className={styles.addButton}
                              data-testid="additional-info-section-add-button"
                              onClick={e => {
                                e.stopPropagation();
                                handleOpenAdditionalInfoPopup();
                              }}
                            >
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none"
                              >
                                <path
                                  d="M8 3V13M3 8H13"
                                  stroke="#909090"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                />
                              </svg>
                            </button>
                          </div>
                        </EditOnlyWrapper>
                      </div>
                      {/* Always render AdditionalInfo component so the ref is available */}
                      <AdditionalInfo
                        ref={additionalInfoRef}
                        links={
                          Array.isArray(
                            (getDataWithFallback() as any)?.additionalInfo
                          )
                            ? (getDataWithFallback() as any).additionalInfo
                            : []
                        }
                        isExpanded={isAdditionalInfoExpanded}
                        issueId={card?.issueId}
                        onAddLink={async newLink => {
                          try {
                            // Use incremental add method - only sends the new link
                            return await apiDataUpdater.addAdditionalInfoLink(
                              newLink
                            );
                          } catch (error: any) {
                            console.error(
                              'âŒ Error adding additional info link:',
                              error
                            );

                            // Extract error message from API response
                            let errorMessage =
                              'Failed to add additional info link. Please try again.';

                            if (error?.response?.data?.message) {
                              // Use the message from the API response
                              errorMessage = error.response.data.message;
                            } else if (error?.message) {
                              // Fallback to error.message if available
                              errorMessage = error.message;
                            }

                            // Show error toast with the API message
                            ToastQueue.negative(errorMessage, {
                              timeout: 5000
                            });
                            throw error;
                          }
                        }}
                        onEditLink={async updatedLink => {
                          try {
                            // Use incremental update method - only sends the updated link
                            return await apiDataUpdater.updateAdditionalInfoLink(
                              updatedLink
                            );
                          } catch (error: any) {
                            console.error(
                              'âŒ Error updating additional info link:',
                              error
                            );

                            // Extract error message from API response
                            let errorMessage =
                              'Failed to update additional info link. Please try again.';

                            if (error?.response?.data?.message) {
                              // Use the message from the API response
                              errorMessage = error.response.data.message;
                            } else if (error?.message) {
                              // Fallback to error.message if available
                              errorMessage = error.message;
                            }

                            // Show error toast with the API message
                            ToastQueue.negative(errorMessage, {
                              timeout: 5000
                            });
                            throw error;
                          }
                        }}
                        onDeleteLink={async linkId => {
                          try {
                            // Use delete method with _delete flag
                            return await apiDataUpdater.deleteAdditionalInfoLink(
                              linkId
                            );
                          } catch (error: any) {
                            console.error(
                              'âŒ Error deleting additional info link:',
                              error
                            );

                            // Extract error message from API response
                            let errorMessage =
                              'Failed to delete additional info link. Please try again.';

                            if (error?.response?.data?.message) {
                              // Use the message from the API response
                              errorMessage = error.response.data.message;
                            } else if (error?.message) {
                              // Fallback to error.message if available
                              errorMessage = error.message;
                            }

                            // Show error toast with the API message
                            ToastQueue.negative(errorMessage, {
                              timeout: 5000
                            });
                            throw error;
                          }
                        }}
                        onDataRefresh={seamlessRefreshWithoutFeatures}
                      />
                    </div>

                    <ChangeLogSection
                      issueId={card?.issueId}
                      isOpen={isOpen}
                      isExpanded={isChangeLogExpanded}
                      onToggle={toggleChangeLog}
                    />
                  </div>
                </>
              ) : (
                <>
                  {/* New Moment Type Accordion for Non-Phased Layout */}
                  {nonPhasedAccordion}

                  <div
                    className={`${styles.additionalSections} ${
                      areAllSectionsCollapsed() ? styles.allCollapsed : ''
                    }`}
                  >
                    <div className={styles.section}>
                      <div
                        className={`${styles.sectionHeader} ${
                          isOverviewExpanded ? styles.sectionHeaderExpanded : ''
                        }`}
                        onClick={toggleOverview}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className={styles.phaseToggle}>
                          <ChevronIcon isExpanded={isOverviewExpanded} />
                        </div>
                        <span className={styles.sectionTitle}>Overview</span>
                        <GeneralInfoCommentIcon
                          phaseName={'Overview'}
                          onClick={() =>
                            handleGeneralInfoCommentIconClick({
                              phaseName: 'Overview'
                            })
                          }
                        />
                      </div>
                      {isOverviewExpanded && (
                        <div className={styles.sectionContent}>
                          {renderOverviewSection()}
                        </div>
                      )}
                    </div>

                    {/* Features section - only for Marketing Moment */}
                    {momentType === 'Marketing Moment' && (
                      <FeaturesSectionReadonlyWrapper
                        card={card}
                        targetFeatureId={targetFeatureId}
                        targetProductId={targetProductId}
                        forceExpanded={
                          navigationContext.type === 'feature-click'
                        }
                        isOpen={isOpen}
                      />
                    )}

                    <div className={styles.section}>
                      <div
                        className={`${styles.sectionHeader} ${
                          isDecisionsExpanded
                            ? styles.sectionHeaderExpanded
                            : ''
                        }`}
                        style={{ cursor: 'pointer' }}
                      >
                        <div
                          onClick={toggleDecisions}
                          style={{
                            display: 'flex',
                            alignItems: 'center'
                          }}
                        >
                          <div className={styles.phaseToggle}>
                            <ChevronIcon isExpanded={isDecisionsExpanded} />
                          </div>
                          <span className={styles.sectionTitle}>Decisions</span>
                          <GeneralInfoCommentIcon
                            phaseName={'Decisions'}
                            onClick={() =>
                              handleGeneralInfoCommentIconClick({
                                phaseName: 'Decisions'
                              })
                            }
                          />
                        </div>
                        {isDecisionsExpanded && (
                          <EditOnlyWrapper>
                            <div className={styles.sectionEditIcon}>
                              <button
                                className={styles.addButton}
                                onClick={e => {
                                  e.stopPropagation();
                                  handleOpenDecisionPopup();
                                }}
                              >
                                <svg
                                  width="16"
                                  height="16"
                                  viewBox="0 0 16 16"
                                  fill="none"
                                >
                                  <path
                                    d="M8 3V13M3 8H13"
                                    stroke="#909090"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                  />
                                </svg>
                              </button>
                            </div>
                          </EditOnlyWrapper>
                        )}
                      </div>
                      {/* Always render Decisions component so the ref is available */}
                      <Decisions
                        decisions={
                          (getDataWithFallback() as any)?.decisions || []
                        }
                        issueId={card?.issueId}
                        addDecisionRef={addDecisionRef as any}
                        isExpanded={isDecisionsExpanded}
                        momentName={momentName}
                        momentType={momentType}
                        onError={error => {
                          setErrorMessage(error);
                          setShowErrorBanner(true);
                          // Auto-hide error banner after 5 seconds
                          setTimeout(() => {
                            setShowErrorBanner(false);
                            setErrorMessage('');
                          }, 5000);
                        }}
                        onDataRefresh={seamlessRefreshWithoutFeatures}
                      />
                    </div>

                    <div className={styles.section}>
                      <div
                        className={`${styles.sectionHeader} ${
                          isDACIExpanded ? styles.sectionHeaderExpanded : ''
                        }`}
                        style={{ cursor: 'pointer' }}
                      >
                        <div
                          onClick={toggleDACI}
                          style={{
                            display: 'flex',
                            alignItems: 'center'
                          }}
                        >
                          <div className={styles.phaseToggle}>
                            <ChevronIcon isExpanded={isDACIExpanded} />
                          </div>
                          <span className={styles.sectionTitle}>
                            DACI Contacts
                          </span>
                          <GeneralInfoCommentIcon
                            phaseName={'DACI Contacts'}
                            onClick={() =>
                              handleGeneralInfoCommentIconClick({
                                phaseName: 'DACI Contacts'
                              })
                            }
                          />
                        </div>
                        <EditOnlyWrapper>
                          <div className={styles.sectionEditIcon}>
                            <button
                              className={styles.addButton}
                              onClick={e => {
                                e.stopPropagation();
                                handleOpenDACIPopup();
                              }}
                            >
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none"
                              >
                                <path
                                  d="M8 3V13M3 8H13"
                                  stroke="#909090"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                />
                              </svg>
                            </button>
                          </div>
                        </EditOnlyWrapper>
                      </div>
                      {/* Always render DACIContacts component so the popup is available */}
                      <DACIContacts
                        daciData={daciContacts}
                        isExpanded={isDACIExpanded}
                        onAddContact={handleAddDACIContact}
                        onEditContact={handleEditDACIContact}
                        onDeleteContact={handleDeleteDACIContact}
                      />
                    </div>

                    <div className={styles.section}>
                      <div
                        className={`${styles.sectionHeader} ${
                          isAdditionalInfoExpanded
                            ? styles.sectionHeaderExpanded
                            : ''
                        }`}
                        style={{ cursor: 'pointer' }}
                      >
                        <div
                          onClick={toggleAdditionalInfo}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            flex: 1
                          }}
                        >
                          <div className={styles.phaseToggle}>
                            <ChevronIcon
                              isExpanded={isAdditionalInfoExpanded}
                            />
                          </div>
                          <span className={styles.sectionTitle}>
                            Additional Info
                          </span>
                          <GeneralInfoCommentIcon
                            phaseName={'Additional Info'}
                            onClick={() =>
                              handleGeneralInfoCommentIconClick({
                                phaseName: 'Additional Info'
                              })
                            }
                          />
                          <EditOnlyWrapper>
                            <div className={styles.sectionEditIcon}>
                              <button
                                className={styles.addButton}
                                data-testid="additional-info-section-add-button"
                                onClick={e => {
                                  e.stopPropagation();
                                  handleOpenAdditionalInfoPopup();
                                }}
                              >
                                <svg
                                  width="16"
                                  height="16"
                                  viewBox="0 0 16 16"
                                  fill="none"
                                >
                                  <path
                                    d="M8 3V13M3 8H13"
                                    stroke="#909090"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                  />
                                </svg>
                              </button>
                            </div>
                          </EditOnlyWrapper>
                        </div>
                      </div>
                      {/* Always render AdditionalInfo component so the ref is available */}
                      <AdditionalInfo
                        ref={additionalInfoRef}
                        links={
                          Array.isArray(
                            (getDataWithFallback() as any)?.additionalInfo
                          )
                            ? (getDataWithFallback() as any).additionalInfo
                            : []
                        }
                        isExpanded={isAdditionalInfoExpanded}
                        issueId={card?.issueId}
                        onAddLink={async newLink => {
                          try {
                            // Use incremental add method - only sends the new link
                            return await apiDataUpdater.addAdditionalInfoLink(
                              newLink
                            );
                          } catch (error: any) {
                            console.error(
                              'âŒ Error adding additional info link:',
                              error
                            );

                            // Extract error message from API response
                            let errorMessage =
                              'Failed to add additional info link. Please try again.';

                            if (error?.response?.data?.message) {
                              // Use the message from the API response
                              errorMessage = error.response.data.message;
                            } else if (error?.message) {
                              // Fallback to error.message if available
                              errorMessage = error.message;
                            }

                            // Show error toast with the API message
                            ToastQueue.negative(errorMessage, {
                              timeout: 5000
                            });
                            throw error;
                          }
                        }}
                        onEditLink={async updatedLink => {
                          try {
                            // Use incremental update method - only sends the updated link
                            return await apiDataUpdater.updateAdditionalInfoLink(
                              updatedLink
                            );
                          } catch (error: any) {
                            console.error(
                              'âŒ Error updating additional info link:',
                              error
                            );

                            // Extract error message from API response
                            let errorMessage =
                              'Failed to update additional info link. Please try again.';

                            if (error?.response?.data?.message) {
                              // Use the message from the API response
                              errorMessage = error.response.data.message;
                            } else if (error?.message) {
                              // Fallback to error.message if available
                              errorMessage = error.message;
                            }

                            // Show error toast with the API message
                            ToastQueue.negative(errorMessage, {
                              timeout: 5000
                            });
                            throw error;
                          }
                        }}
                        onDeleteLink={async linkId => {
                          try {
                            // Use delete method with _delete flag
                            return await apiDataUpdater.deleteAdditionalInfoLink(
                              linkId
                            );
                          } catch (error: any) {
                            console.error(
                              'âŒ Error deleting additional info link:',
                              error
                            );

                            // Extract error message from API response
                            let errorMessage =
                              'Failed to delete additional info link. Please try again.';

                            if (error?.response?.data?.message) {
                              // Use the message from the API response
                              errorMessage = error.response.data.message;
                            } else if (error?.message) {
                              // Fallback to error.message if available
                              errorMessage = error.message;
                            }

                            // Show error toast with the API message
                            ToastQueue.negative(errorMessage, {
                              timeout: 5000
                            });
                            throw error;
                          }
                        }}
                        onDataRefresh={seamlessRefreshWithoutFeatures}
                      />
                    </div>

                    <ChangeLogSection
                      issueId={card?.issueId}
                      isOpen={isOpen}
                      isExpanded={isChangeLogExpanded}
                      onToggle={toggleChangeLog}
                    />
                  </div>
                </>
              )}
            </>
          )}
          {activeSection === 'features' && featuresContent}
        </div>
      </div>
    </View>
  );
}
